<script setup>
import { watch, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import TicketsTablePage from 'dashboard/components/tickets/TicketsTablePage.vue';
import TicketDetail from 'dashboard/components/tickets/TicketDetail.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  ticketId: {
    type: Number,
    default: 0,
  },
});

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const selectedTicket = useMapGetter('supportTickets/getSelectedSupportTicket');
const detailLoading = useMapGetter(
  'supportTickets/getSupportTicketDetailLoading'
);

const hasSelectedTicket = computed(() => Boolean(selectedTicket.value?.id));

const findTicketInStore = () => {
  return store.getters['supportTickets/getSupportTickets'].find(
    ticket => Number(ticket.id) === Number(props.ticketId)
  );
};

const loadTicketDetail = async () => {
  if (!props.ticketId) {
    store.dispatch('supportTickets/clearActiveSupportTicket');
    return;
  }

  const ticket = findTicketInStore();
  if (ticket?.id && ticket.dataFetched) {
    await store.dispatch('supportTickets/setActiveSupportTicket', ticket);
    return;
  }

  await store.dispatch(
    'supportTickets/loadSupportTicketDetail',
    props.ticketId
  );
};

watch(
  () => props.ticketId,
  () => loadTicketDetail()
);

onMounted(async () => {
  await store.dispatch('agents/get');

  if (!props.ticketId) {
    await store.dispatch('supportTickets/fetchSupportTickets');
    return;
  }

  await loadTicketDetail();
});

const onTicketSelected = ticket => {
  router.push({
    name: 'support_ticket_show',
    params: {
      accountId: route.params.accountId,
      ticketId: ticket.id,
    },
  });
};

const backToList = () => {
  router.push({
    name: 'support_tickets_list',
    params: { accountId: route.params.accountId },
  });
};
</script>

<template>
  <section class="flex w-full h-full min-w-0">
    <TicketsTablePage v-if="!ticketId" @select-ticket="onTicketSelected" />
    <div v-else class="flex flex-col flex-1 min-w-0 h-full">
      <div class="px-4 py-3 border-b shrink-0 border-n-weak">
        <NextButton
          icon="i-lucide-arrow-left"
          faded
          slate
          sm
          :label="t('SUPPORT_TICKETS.BACK_TO_LIST')"
          @click="backToList"
        />
      </div>
      <div
        v-if="detailLoading"
        class="flex flex-col items-center justify-center flex-1"
      >
        <Spinner />
      </div>
      <TicketDetail
        v-else-if="hasSelectedTicket"
        :ticket="selectedTicket"
        is-on-expanded-layout
      />
    </div>
  </section>
</template>
